import type { FC } from 'react';
import { Button, Inline, Tooltip } from '@george-labs.com/design-system';

import { t } from '@luna/i18n';
import { copyText } from '@luna/utils';

import type { CommonItemProps } from './types';
import { ProductsListItemWrapper } from '../ProductsListItemWrapper';

type CopyLabelKey =
    | ''
    | 'account.details.copyAccountNumber'
    | 'account.details.copyBban'
    | 'account.details.copyBic'
    | 'account.details.copyIban'
    | 'account.details.copyLoanIdentifier';

type ExtraTooltipLabelKey = 'products.savings.ibanTooltip';

type Copyable = {
    copyable: true;
    copyLabelKey: CopyLabelKey;
    onCopy: () => void;
};

type NotCopyable = {
    copyable?: never | false;
    copyLabelKey?: never;
    onCopy?: never;
};

type Props = CommonItemProps & {
    content: string | number;
    bold?: boolean;
    extraTooltip?: boolean;
    copyable?: boolean;
    extraTooltipLabelKey?: ExtraTooltipLabelKey;
} & (Copyable | NotCopyable);

const COPY_LABELS: Record<CopyLabelKey, () => string> = {
    '': () => '',
    'account.details.copyAccountNumber': () => t('account.details.copyAccountNumber'),
    'account.details.copyBban': () => t('account.details.copyBban'),
    'account.details.copyBic': () => t('account.details.copyBic'),
    'account.details.copyIban': () => t('account.details.copyIban'),
    'account.details.copyLoanIdentifier': () => t('account.details.copyLoanIdentifier'),
};

const getCopyLabel = (copyLabelKey: CopyLabelKey | '') => {
    if (copyLabelKey) {
        return COPY_LABELS[copyLabelKey]?.() ?? copyLabelKey;
    }

    return '';
};

const EXTRA_TOOLTIP_LABELS: Record<ExtraTooltipLabelKey, () => string> = {
    'products.savings.ibanTooltip': () => t('products.savings.ibanTooltip'),
};

const getExtraTooltipLabel = (extraTooltipLabelKey: ExtraTooltipLabelKey) => {
    return EXTRA_TOOLTIP_LABELS[extraTooltipLabelKey]();
};

export const ProductsListTextItem: FC<Props> = ({
    'data-cy': dataCy,
    className,
    label,
    labelClassName,
    labelColumnWidth,
    content,
    bold = false,
    copyable = false,
    extraTooltip,
    extraTooltipLabelKey,
    ...rest
}) => {
    const { copyLabelKey = '', onCopy } = rest as Copyable;
    const copyLabel = getCopyLabel(copyLabelKey);

    const handleOnClick = async () => {
        await copyText(`${content}`);
        onCopy?.();
    };

    return (
        <ProductsListItemWrapper
            data-cy={dataCy}
            className={className}
            label={label}
            labelClassName={labelClassName}
            labelColumnWidth={labelColumnWidth}
        >
            <Inline data-cy={dataCy} emphasis={bold}>
                {content}
            </Inline>
            {copyable && (
                <Tooltip content={copyLabel}>
                    <Button
                        data-cy={`${dataCy}-copy-button`}
                        className="pl-1 pb-1"
                        size={Button.SIZE.SMALL}
                        icon={Button.ICON.COPY}
                        onClick={handleOnClick}
                        variant={Button.VARIANT.TERTIARY}
                        fullWidth={Button.FULL_WIDTH.NEVER}
                        hiddenLabel={Button.HIDDEN_LABEL.ALWAYS}
                    >
                        {copyLabel}
                    </Button>
                </Tooltip>
            )}
            {extraTooltip && extraTooltipLabelKey && (
                <Tooltip
                    content={getExtraTooltipLabel(extraTooltipLabelKey)}
                    className="ml-1 mb-1"
                />
            )}
        </ProductsListItemWrapper>
    );
};
